import {
  AddedToBookedSuppliersAction,
  ShortlistActionTypes,
} from '@bridebook/web/lib/shortlist/action-types';
import { isSupplierVenue } from '@bridebook/web/lib/supplier/utils';
import { Action, IApplicationState, IEpicDeps } from '@bridebook/web/lib/types';
import { venueSection } from '@bridebook/web/lib/wedding-website/constants/emptyWebsiteTemplate';
import { SECTION_NAMES } from '@bridebook/web/lib/wedding-website/constants';
import {
  updatePublishedPrimaryVenueSupplier,
  updateWebsiteSection,
} from '@bridebook/web/lib/weddings/actions';
import { getWebsiteSection } from '@bridebook/web/lib/weddings/selectors';
import { omit } from 'lodash';
import { ofType } from 'redux-observable';
import { Observable, of } from 'rxjs';
import { filter, mergeMap, withLatestFrom } from 'rxjs/operators';
import getLastBookedVenue from '../utils/get-last-booked-venue';
import { TShortlistList } from '../../shortlist/types';

const getNewSupplier = (action: AddedToBookedSuppliersAction, state: IApplicationState) => {
  const venue = action.payload.item;
  if (action.type === ShortlistActionTypes.ADDED_TO_BOOKED_SUPPLIERS) {
    return venue.id;
  }

  // for unbooking, we need to get the last booked venue
  const shortlistWithoutCurrentVenue = omit(state.shortlist.list, venue.id) as TShortlistList;
  const lastBookedVenue = getLastBookedVenue(shortlistWithoutCurrentVenue);
  return lastBookedVenue?.id ?? '';
};

/**
 * Epic that syncs booked venues to the wedding website venue section.
 * Listens to ADDED_TO_BOOKED_SUPPLIERS and UNBOOK_SUPPLIER and updates both
 * the draft (via updateWebsiteSection) and the published website
 * (via updatePublishedPrimaryVenueSupplier) when a venue is marked as booked/unbooked.
 */
export const syncBookedVenueToWebsiteEpic = (
  action$: Observable<AddedToBookedSuppliersAction>,
  { state$ }: IEpicDeps,
): Observable<any> =>
  action$.pipe(
    ofType(ShortlistActionTypes.ADDED_TO_BOOKED_SUPPLIERS, ShortlistActionTypes.UNBOOK_SUPPLIER),
    withLatestFrom(state$),
    filter(([action]) => {
      const { item } = action.payload;
      const isVenue = isSupplierVenue(item);
      return isVenue;
    }),
    mergeMap(([action, state]: [AddedToBookedSuppliersAction, IApplicationState]) => {
      const websiteVenueSection = getWebsiteSection.VenueField(SECTION_NAMES.PRIMARY_VENUE)(state);
      const useVenuePhotos = websiteVenueSection?.data?.useVenuePhotos;
      const newSupplier = getNewSupplier(action, state);

      // This ensures both the editor draft and the published website reflect the last booked venue
      return of<Action>(
        updateWebsiteSection({
          name: venueSection.name,
          type: venueSection.type,
          data: {
            supplier: newSupplier,
            useVenuePhotos: useVenuePhotos ?? false,
          },
        }),
        updatePublishedPrimaryVenueSupplier({
          supplier: newSupplier,
        }),
      );
    }),
  );
